import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowRight,
  CheckCircle,
  XLogo,
  InstagramLogo,
  TiktokLogo,
} from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { contactApi } from "../../api/contact";
import { ApiError } from "../../api/client";
import { COUNTRY_DIAL_CODES, DEFAULT_DIAL_CODE } from "./dialCodes";

const buildSchema = (t) =>
  z.object({
    motif: z.enum(
      ["formation", "formation_gestion", "gestion", "autre"],
      { errorMap: () => ({ message: t("contact.form.error_motif") }) },
    ),
    first_name: z.string().trim().min(1, t("contact.form.error_required")),
    last_name: z.string().trim().min(1, t("contact.form.error_required")),
    dial_code: z.string().min(2),
    phone: z.string().trim().min(6, t("contact.form.error_phone")),
    email: z.string().trim().email(t("contact.form.error_email")),
    x_handle: z.string().trim().max(60).optional().or(z.literal("")),
    instagram_handle: z.string().trim().max(60).optional().or(z.literal("")),
    tiktok_handle: z.string().trim().max(60).optional().or(z.literal("")),
    website: z.string().trim().max(200).optional().or(z.literal("")),
    message: z.string().trim().min(10, t("contact.form.error_message_min")),
  });

const FieldError = ({ children }) => (
  <p className="mt-1.5 text-xs text-red-600">{children}</p>
);

const RequiredLabel = ({ htmlFor, children }) => (
  <Label
    htmlFor={htmlFor}
    className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700"
  >
    {children}
    <span aria-hidden="true" className="ml-1 text-rose-500">
      *
    </span>
  </Label>
);

export const ContactForm = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  // Persist the "sent" state for the current browser session so refreshes
  // (or accidental clicks on the still-visible form) don't allow a duplicate
  // submission. The flag disappears when the tab is closed.
  useEffect(() => {
    if (sessionStorage.getItem("contact_form_sent") === "1") {
      setStatus("success");
    }
  }, []);

  const schema = buildSchema(t);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      motif: "",
      first_name: "",
      last_name: "",
      dial_code: DEFAULT_DIAL_CODE,
      phone: "",
      email: "",
      x_handle: "",
      instagram_handle: "",
      tiktok_handle: "",
      website: "",
      message: "",
    },
  });

  const motifValue = watch("motif");
  const dialValue = watch("dial_code");

  const onSubmit = useCallback(
    async (data) => {
      setStatus("submitting");
      setErrorMsg("");
      try {
        const country = COUNTRY_DIAL_CODES.find(
          (c) => c.code === data.dial_code,
        );
        // Strip any leading "+<dial>" or "+" the user may have typed,
        // so the country prefix is not duplicated.
        let localPhone = data.phone.trim();
        if (country && localPhone.startsWith(country.dial)) {
          localPhone = localPhone.slice(country.dial.length).trim();
        } else if (localPhone.startsWith("+")) {
          localPhone = localPhone.replace(/^\+\d+\s*/, "");
        }
        const payload = {
          motif: data.motif,
          first_name: data.first_name,
          last_name: data.last_name,
          phone: `${country ? country.dial : ""} ${localPhone}`.trim(),
          email: data.email,
          message: data.message,
        };
        const trimmed = (v) => (v || "").trim();
        if (trimmed(data.x_handle)) payload.x_handle = trimmed(data.x_handle);
        if (trimmed(data.instagram_handle))
          payload.instagram_handle = trimmed(data.instagram_handle);
        if (trimmed(data.tiktok_handle))
          payload.tiktok_handle = trimmed(data.tiktok_handle);
        // honeypot: send the value, server uses it to flag spam
        if (trimmed(data.website)) payload.website = trimmed(data.website);

        // One transparent retry on transient errors (network / 5xx).
        // The backend deduplicates identical submissions within 5 minutes,
        // so retrying is safe and never creates duplicate inbox entries.
        try {
          await contactApi.submit(payload);
        } catch (err) {
          const transient = !err.status || err.status >= 500;
          if (!transient) throw err;
          await new Promise((r) => setTimeout(r, 700));
          await contactApi.submit(payload);
        }
        sessionStorage.setItem("contact_form_sent", "1");
        setStatus("success");
      } catch (err) {
        if (err instanceof ApiError && err.status === 429) {
          setErrorMsg(t("contact.form.error_rate_limit"));
        } else {
          setErrorMsg(t("contact.form.error_generic"));
        }
        setStatus("error");
      }
    },
    [t],
  );

  const reopen = useCallback(() => {
    sessionStorage.removeItem("contact_form_sent");
    reset();
    setStatus("idle");
    setErrorMsg("");
  }, [reset]);

  if (status === "success") {
    return (
      <div
        data-testid="contact-form-success"
        className="rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-50/40 p-8 md:p-12 text-center"
      >
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/30">
          <CheckCircle size={26} weight="fill" className="text-white" />
        </div>
        <h3 className="mt-6 font-heading text-2xl md:text-3xl font-bold tracking-tight text-emerald-900">
          {t("contact.form.success_title")}
        </h3>
        <p className="mt-3 text-sm md:text-base leading-relaxed text-emerald-800/80 max-w-md mx-auto">
          {t("contact.form.success_message")}
        </p>

        {/* Locked confirmation pill — replaces the form CTA so the user knows
            the message is in. Same shape as the submit button to keep visual
            continuity. */}
        <div
          data-testid="contact-form-sent-badge"
          aria-disabled="true"
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-7 py-4 text-sm font-bold text-white cursor-not-allowed select-none"
        >
          <CheckCircle size={16} weight="fill" />
          {t("contact.form.sent_badge")}
        </div>

        <div className="mt-6">
          <Link
            to="/"
            data-testid="contact-form-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-emerald-900 hover:text-emerald-700 underline underline-offset-4 decoration-emerald-300 hover:decoration-emerald-700 transition-colors"
          >
            {t("placeholder.back")}
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="relative rounded-3xl border-2 border-slate-200 bg-white p-6 md:p-10"
    >
      <div className="grid grid-cols-1 gap-5">
        {/* Motif */}
        <div>
          <RequiredLabel htmlFor="motif">
            {t("contact.form.motif_label")}
          </RequiredLabel>
          <Select
            value={motifValue || undefined}
            onValueChange={(v) =>
              setValue("motif", v, { shouldValidate: true })
            }
          >
            <SelectTrigger
              id="motif"
              data-testid="motif-trigger"
              className="mt-2 h-12 rounded-xl border-slate-200"
            >
              <SelectValue placeholder={t("contact.form.motif_placeholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="formation" data-testid="motif-formation">
                {t("contact.form.motif_formation")}
              </SelectItem>
              <SelectItem
                value="formation_gestion"
                data-testid="motif-formation-gestion"
              >
                {t("contact.form.motif_formation_gestion")}
              </SelectItem>
              <SelectItem value="gestion" data-testid="motif-gestion">
                {t("contact.form.motif_gestion")}
              </SelectItem>
              <SelectItem value="autre" data-testid="motif-autre">
                {t("contact.form.motif_autre")}
              </SelectItem>
            </SelectContent>
          </Select>
          {errors.motif && <FieldError>{errors.motif.message}</FieldError>}
        </div>

        {/* First / Last name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <RequiredLabel htmlFor="first_name">
              {t("contact.form.first_name_label")}
            </RequiredLabel>
            <Input
              id="first_name"
              data-testid="contact-first-name"
              autoComplete="given-name"
              placeholder={t("contact.form.first_name_placeholder")}
              className="mt-2 h-12 rounded-xl border-slate-200"
              {...register("first_name")}
            />
            {errors.first_name && (
              <FieldError>{errors.first_name.message}</FieldError>
            )}
          </div>
          <div>
            <RequiredLabel htmlFor="last_name">
              {t("contact.form.last_name_label")}
            </RequiredLabel>
            <Input
              id="last_name"
              data-testid="contact-last-name"
              autoComplete="family-name"
              placeholder={t("contact.form.last_name_placeholder")}
              className="mt-2 h-12 rounded-xl border-slate-200"
              {...register("last_name")}
            />
            {errors.last_name && (
              <FieldError>{errors.last_name.message}</FieldError>
            )}
          </div>
        </div>

        {/* Phone / Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <RequiredLabel htmlFor="phone">
              {t("contact.form.phone_label")}
            </RequiredLabel>
            <div className="mt-2 flex gap-2">
              <Select
                value={dialValue}
                onValueChange={(v) =>
                  setValue("dial_code", v, { shouldValidate: true })
                }
              >
                <SelectTrigger
                  data-testid="contact-dial-trigger"
                  aria-label={t("contact.form.dial_code_label")}
                  className="h-12 w-[120px] shrink-0 rounded-xl border-slate-200"
                >
                  <SelectValue>
                    {(() => {
                      const c = COUNTRY_DIAL_CODES.find(
                        (x) => x.code === dialValue,
                      );
                      return c ? (
                        <span className="flex items-center gap-1.5">
                          <span aria-hidden="true" className="text-base leading-none">
                            {c.flag}
                          </span>
                          <span className="text-sm font-medium text-slate-700">
                            {c.dial}
                          </span>
                        </span>
                      ) : null;
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {COUNTRY_DIAL_CODES.map((c) => (
                    <SelectItem
                      key={c.code}
                      value={c.code}
                      data-testid={`dial-${c.code}`}
                    >
                      <span className="flex items-center gap-2">
                        <span aria-hidden="true" className="text-base leading-none">
                          {c.flag}
                        </span>
                        <span className="text-sm text-slate-700">
                          {c.name}
                        </span>
                        <span className="text-xs text-slate-500">{c.dial}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="phone"
                type="tel"
                data-testid="contact-phone"
                autoComplete="tel-national"
                placeholder={t("contact.form.phone_placeholder")}
                className="h-12 rounded-xl border-slate-200 flex-1"
                {...register("phone")}
              />
            </div>
            {errors.phone && <FieldError>{errors.phone.message}</FieldError>}
          </div>
          <div>
            <RequiredLabel htmlFor="email">
              {t("contact.form.email_label")}
            </RequiredLabel>
            <Input
              id="email"
              type="email"
              data-testid="contact-email"
              autoComplete="email"
              placeholder={t("contact.form.email_placeholder")}
              className="mt-2 h-12 rounded-xl border-slate-200"
              {...register("email")}
            />
            {errors.email && <FieldError>{errors.email.message}</FieldError>}
          </div>
        </div>

        {/* Social handles (optional) */}
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">
            {t("contact.form.socials_label")}
          </Label>
          <p className="mt-1 text-xs text-slate-500">
            {t("contact.form.socials_help")}
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-slate-500">
                <XLogo size={14} weight="regular" />
                <span className="text-sm">@</span>
              </span>
              <Input
                id="x_handle"
                data-testid="contact-x"
                placeholder={t("contact.form.x_placeholder")}
                className="h-12 rounded-xl border-slate-200 pl-12"
                {...register("x_handle")}
              />
            </div>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-slate-500">
                <InstagramLogo size={14} weight="regular" />
                <span className="text-sm">@</span>
              </span>
              <Input
                id="instagram_handle"
                data-testid="contact-instagram"
                placeholder={t("contact.form.instagram_placeholder")}
                className="h-12 rounded-xl border-slate-200 pl-12"
                {...register("instagram_handle")}
              />
            </div>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-slate-500">
                <TiktokLogo size={14} weight="regular" />
                <span className="text-sm">@</span>
              </span>
              <Input
                id="tiktok_handle"
                data-testid="contact-tiktok"
                placeholder={t("contact.form.tiktok_placeholder")}
                className="h-12 rounded-xl border-slate-200 pl-12"
                {...register("tiktok_handle")}
              />
            </div>
          </div>
        </div>

        {/* Message */}
        <div>
          <RequiredLabel htmlFor="message">
            {t("contact.form.message_label")}
          </RequiredLabel>
          <Textarea
            id="message"
            data-testid="contact-message"
            rows={6}
            placeholder={t("contact.form.message_placeholder")}
            className="mt-2 rounded-xl border-slate-200 resize-none"
            {...register("message")}
          />
          {errors.message && (
            <FieldError>{errors.message.message}</FieldError>
          )}
        </div>

        {/* Honeypot — hidden from real users, bots will fill it. */}
        <div
          aria-hidden="true"
          className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden"
        >
          <label htmlFor="hp-website">Website</label>
          <input
            id="hp-website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            {...register("website")}
          />
        </div>

        {errorMsg && (
          <div
            data-testid="contact-form-error"
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {errorMsg}
          </div>
        )}

        <button
          type="submit"
          data-testid="contact-submit"
          disabled={status === "submitting"}
          className="group inline-flex items-center justify-center gap-2 rounded-full adonis-gradient px-7 py-4 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#7B61FF]/30 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {status === "submitting"
            ? t("contact.form.submitting")
            : t("contact.form.submit")}
          {status !== "submitting" && (
            <ArrowRight
              size={14}
              weight="bold"
              className="transition-transform group-hover:translate-x-1"
            />
          )}
        </button>

        <p className="text-xs leading-relaxed text-slate-500 text-center max-w-2xl mx-auto">
          {t("contact.form.consent")}
        </p>
      </div>
    </form>
  );
};

export default ContactForm;
